import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowRight } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import z from 'zod'

import useErrorMessage from '@hooks/use-error-message'

import { Button } from '@shadcn/button'
import { Checkbox } from '@shadcn/checkbox'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@shadcn/input-otp'
import { Label } from '@shadcn/label'

import {
  useMutationSession2FAResend,
  useMutationSession2FAVerify,
} from '@api/session/session.mutations'

const searchSchema = z.object({
  maskedEmail: z.string(),
})

export const Route = createFileRoute('/session/login/2fa/')({
  component: RouteComponent,
  validateSearch: searchSchema,
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const errorMessage = useErrorMessage()
  const { maskedEmail } = Route.useSearch()

  const [code, setCode] = useState('')
  const [trustDevice, setTrustDevice] = useState(false)

  const { mutate: verify, isPending: isVerifying } =
    useMutationSession2FAVerify()
  const { mutate: resend, isPending: isResending } =
    useMutationSession2FAResend()

  const handleSubmit = () => {
    if (code.length !== 6) return

    verify(
      { code, trustDevice },
      {
        onSuccess: () => navigate({ to: '/app' }),
        onError: (error) => {
          const message = errorMessage({ error })
          toast.error(message)
          setCode('')
        },
      },
    )
  }

  const handleResend = () => {
    resend(undefined, {
      onSuccess: () => {
        toast.success(t('session.two-factor.resend.success'))
        setCode('')
      },
      onError: (error) => {
        const message = errorMessage({ error })
        toast.error(message)
      },
    })
  }

  return (
    <div className="flex w-full max-w-82 flex-col gap-6 rounded-md border bg-white p-4 shadow-lg">
      <h1 className="text-lg font-bold">{t('session.two-factor.title')}</h1>
      <p className="text-text-secondary-700 text-sm">
        {maskedEmail
          ? t('session.two-factor.description', { email: maskedEmail })
          : t('session.two-factor.description-fallback')}
      </p>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col items-center gap-2">
          <InputOTP
            maxLength={6}
            value={code}
            onChange={setCode}
            onComplete={handleSubmit}
            autoFocus
          >
            <InputOTPGroup>
              {Array.from({ length: 6 }, (_, i) => (
                <InputOTPSlot key={`input-opt-slot-${i}`} index={i} />
              ))}
            </InputOTPGroup>
          </InputOTP>

          <div className="flex items-center gap-2">
            <Checkbox
              id="trust-device"
              checked={trustDevice}
              onCheckedChange={(checked) => setTrustDevice(checked === true)}
            />
            <Label htmlFor="trust-device" className="text-sm">
              {t('session.two-factor.trust-device')}
            </Label>
          </div>
        </div>
        <Button
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={code.length !== 6 || isVerifying}
          className="w-full"
        >
          <div className="flex items-center gap-1">
            <span>{t('session.two-factor.submit')}</span>
            <ArrowRight className="size-4 rounded-md" />
          </div>
        </Button>

        <Button
          variant="ghostPrimary"
          size="ghost"
          onClick={handleResend}
          disabled={isResending}
          className="w-full text-sm"
        >
          {t('session.two-factor.resend.label')}
        </Button>
      </div>
    </div>
  )
}
